import { inject, Injectable } from "@angular/core";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  PluginTransaction,
  PluginTransactionsMap,
  PluginTransactionsResponse,
} from "@bitwarden/web-vault/app/shared/utils/helper.plugin/plugin.transactions.map";
import { getPluginMetadata } from "@bitwarden/web-vault/app/models/data/plugin-metadata";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { GlossQuantityUtils } from "@bitwarden/web-vault/app/models/data/shared/gloss-quantity";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { Origin, SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { enrichTransaction } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import {
  collectVaultTransactionIdsForAccount,
  dedupeTransactionsByVaultIds,
  filterPluginTransactionsFromStartDate,
  getIncrementalSyncStartDate,
  getSyncEndDateToday,
} from "@bitwarden/web-vault/app/services/store/data-plugin/australian-super-sync";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";

/** Transactions asked for per page. */
export const PLUGIN_SYNC_PAGE_LIMIT = 100;

/**
 * The most pages fetchAllTransactions follows for one account (100k transactions at
 * PLUGIN_SYNC_PAGE_LIMIT 100). An unbounded pagination (#6023) refetched until the tab ran out of
 * memory, never releasing the batch's sync counters. PLUGIN_SYNC_STUCK_PAGES finds one that isn't
 * moving in a few pages; this bounds the rest: a cursor that keeps moving with no end, or empty
 * pages with no end.
 */
export const PLUGIN_SYNC_MAX_PAGES = 1000;

/**
 * Pages in a row that bring no transaction it hasn't already fetched before fetchAllTransactions
 * gives up: a pagination that isn't moving (stuck on a cursor, or going round a few), found in a
 * few round trips rather than PLUGIN_SYNC_MAX_PAGES of them on every resync.
 */
export const PLUGIN_SYNC_STUCK_PAGES = 3;

/** Error-like rejections (e.g. an ErrorResponse, which isn't an Error) keep their message and
 * name in the stored sync status instead of becoming "[object Object]". */
function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  const { message, name, stack } = (error ?? {}) as Partial<Record<keyof Error, unknown>>;
  const asError = new Error(typeof message === "string" ? message : String(error));
  if (typeof name === "string") {
    asError.name = name;
  }
  // No stack beats one pointing here instead of at the failure.
  asError.stack = typeof stack === "string" ? stack : undefined;
  return asError;
}

export interface PluginAccountSyncOptions {
  /** PluginConfig.id to fetch transactions from. */
  configId: string;
  /** The auth identifier returned by the plugin's "auth" call. */
  identifier: string;
  /** Australian Super returns history before the requested start date; drop it before mapping. */
  filterFromStartDate?: boolean;
  /**
   * Called after an account's transaction fetch/save failure has been logged and stamped (not for
   * CBAT failures, which savePluginCBAT handles itself). Return an error to abort the rest of the
   * batch with it (Australian Super's terminal teardown), or undefined to carry on.
   */
  abortBatchOn?: (error: unknown) => Error | undefined;
}

/**
 * The per-account plugin sync used by PluginConfigComponent (connect/resync): paginated
 * transaction fetch, dedupe against the vault, CBAT, and the sync-status stamp. Extracted when
 * the since-removed SelectPluginComponent carried a drifted copy of it (#5096).
 */
@Injectable({ providedIn: "root" })
export class PluginAccountSyncService {
  private dataPluginStoreService = inject(DataPluginStoreService);
  private transactionStore = inject(TransactionStoreService);
  private appState = inject(AppStateStoreService);
  private glossSyncService = inject(GlossSyncService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  private logService = inject(LogService);

  /** "N account(s) added" toast, named after the plugin's institution, then its display name. */
  notifyAccountsAdded(count: number, pluginId: string): void {
    const metadata = getPluginMetadata(pluginId);
    this.confirmationDialog.notify(
      "success",
      this.i18nService.t("succeeded"),
      this.i18nService.t(
        "pluginAccountsAdded",
        String(count),
        metadata?.institutionName ?? metadata?.displayName ?? pluginId,
      ),
      false,
    );
  }

  /**
   * Fetches and saves each account's new transactions and CBAT. One account failing is stamped
   * against that account and doesn't stop the rest (unless `abortBatchOn` says so). Each account
   * that syncs is stamped `synced` as soon as it finishes. Returns the accounts that synced.
   */
  async syncAccounts(
    accounts: AccountView[],
    options: PluginAccountSyncOptions,
  ): Promise<AccountView[]> {
    const endDate = getSyncEndDateToday();
    const synced: AccountView[] = [];

    // Own runningSyncState's counters for this batch: the terminateSync calls below would
    // otherwise add onto the accountsCount a finished Basiq/Fiskil/Plaid sync left behind and
    // latch isRunning true for the session (#4919).
    this.glossSyncService.claimPluginSync(accounts.length);
    try {
      for (const account of accounts) {
        this.appState.startProcess(
          "importing-data",
          `Pulling transactions of ${account.displayName}`,
        );
        try {
          const startDate = getIncrementalSyncStartDate(
            this.transactionStore.enrichTransaction.collection(),
            account.id,
          );
          const rawTransactions = await this.fetchAllTransactions(
            account,
            options,
            startDate,
            endDate,
          );
          const forMapping = options.filterFromStartDate
            ? filterPluginTransactionsFromStartDate(rawTransactions, startDate)
            : rawTransactions;
          const newTransactions = this.excludeExistingInVault(
            account,
            PluginTransactionsMap.toTransactionView(forMapping, account),
          );
          if (newTransactions.length > 0) {
            await this.transactionStore.transaction.saveToVault(newTransactions, true);
          }

          this.appState.endProcess("importing-data");
          // Unlike Basiq/Plaid/Fiskil, plugin accounts never get a sync-status record otherwise.
          // savePluginCBAT stamps cantSaveCBAT itself on failure; don't overwrite it with synced.
          if (await this.savePluginCBAT(account)) {
            await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
            synced.push(account);
          }
        } catch (error) {
          await this.stampFailure(
            account,
            error,
            "Syncing plugin",
            SyncStatusEnum.cantSaveTransactions,
          );
          this.appState.endProcess("importing-data");
          const abortWith = options.abortBatchOn?.(error);
          if (abortWith) {
            throw abortWith;
          }
        }
      }
    } finally {
      // Always release the counters claimed above, even when an abort unwinds past the loop,
      // or the next sync's guards latch closed (#4919).
      this.glossSyncService.releasePluginSync();
    }
    return synced;
  }

  /**
   * Every page of the account's transactions, each once (by id; the later copy wins, e.g. pending
   * then posted). Bounded (#6023): it throws, saving nothing for the account, past
   * PLUGIN_SYNC_MAX_PAGES pages, or after PLUGIN_SYNC_STUCK_PAGES pages in a row that bring
   * nothing new (no new transaction, and none changed).
   * - Throws rather than stops with what it has: the next sync starts from the newest saved
   *   transaction, so a cut-off history (pages newest-first) would leave an older gap no later
   *   sync fills. syncAccounts stamps the account cantSaveTransactions and goes on with the batch.
   * - Progress is judged by the transactions, never the cursor: a backend paging by a server-side
   *   session may ignore it, and stopping on "the cursor didn't change" would cut it short.
   * - A transaction without an id is dropped (the vault dedupe drops it anyway, so it's logged
   *   here instead). A page with no transaction that has an id is neither a repeat nor progress:
   *   a session-paged backend may walk empty date ranges, and a plugin may send no ids at all
   *   (it synced before, without them); going round a page already fetched and such a page is
   *   still found, and the page cap bounds a backend that sends only such pages.
   * - Ids are kept as strings (plugin JSON isn't checked at runtime: 1 and "1" are one id).
   */
  private async fetchAllTransactions(
    account: AccountView,
    options: PluginAccountSyncOptions,
    startDate: string,
    endDate: string,
  ): Promise<PluginTransaction[]> {
    // By id; setting an id again keeps its place and takes the later copy.
    const byId = new Map<string, PluginTransaction>();
    let cursor = null;
    let hasNext = true;
    let repeats = 0;
    let withoutId = 0;
    // They may be the cause of either failure: said in the error, and logged however it ends.
    const droppedNote = () =>
      withoutId ? ` (${withoutId} transactions without an id were dropped)` : "";
    try {
      for (let page = 0; hasNext; page++) {
        if (page === PLUGIN_SYNC_MAX_PAGES) {
          throw new Error(
            `Plugin transaction pagination exceeded ${PLUGIN_SYNC_MAX_PAGES} pages ` +
              `(${byId.size} transactions): the history is too large or the pagination never ` +
              `ends${droppedNote()}`,
          );
        }
        const response = (await this.dataPluginStoreService.booktTransaction.fetch({
          type: "transactions",
          config: options.configId,
          inputs: {
            id: options.identifier,
            accountId: account.id,
            startDate,
            endDate,
            cursor,
            limit: PLUGIN_SYNC_PAGE_LIMIT,
          },
        })) as PluginTransactionsResponse;
        // GraphQL lists are nullable: a null entry, null nodes, or a null node, is nothing.
        const entries = (response?.transaction ?? []).filter((entry) => entry != null);
        const nodes = entries.flatMap((entry) => entry.nodes ?? []).filter((node) => node != null);
        // More to come if any entry says so, from the first that says where. "" is a cursor like
        // any other (#6393): only a missing one keeps the last.
        const infos = entries.map((entry) => entry.info).filter((info) => info != null);
        hasNext = infos.some((info) => info.hasNext);
        if (hasNext) {
          cursor =
            infos.find((info) => info.hasNext && info.nextCursor != null)?.nextCursor ?? cursor;
        }
        let withId = 0;
        let progress = 0;
        for (const node of nodes) {
          const id = node.transactionId == null ? "" : String(node.transactionId);
          if (!id) {
            withoutId++;
            continue;
          }
          withId++;
          const kept = node.transactionId === id ? node : { ...node, transactionId: id };
          const before = byId.get(id);
          // New, or a later copy that changed (e.g. posted now): both are progress.
          if (!before || JSON.stringify(before) !== JSON.stringify(kept)) {
            progress++;
          }
          byId.set(id, kept);
        }
        if (withId > 0) {
          repeats = progress === 0 ? repeats + 1 : 0;
        }
        if (hasNext && repeats === PLUGIN_SYNC_STUCK_PAGES) {
          throw new Error(
            `Plugin transaction pagination is stuck: ${PLUGIN_SYNC_STUCK_PAGES} pages in a row ` +
              `brought nothing it hadn't already fetched${droppedNote()}`,
          );
        }
      }
    } finally {
      if (withoutId) {
        this.logService.warning(
          `fetchAllTransactions: dropped ${withoutId} plugin transactions without an id`,
        );
      }
    }
    return [...byId.values()];
  }

  private excludeExistingInVault(
    account: AccountView,
    transactions: TransactionView[],
  ): TransactionView[] {
    const existingIds = collectVaultTransactionIdsForAccount(
      this.transactionStore.enrichTransaction.collection(),
      account.id,
    );
    return dedupeTransactionsByVaultIds(transactions, existingIds, this.logService);
  }

  /**
   * Logs the failure and stamps `status` on the account. Pass null to logError so its own
   * isDevMode()-gated terminateSync stays a no-op: terminating here unconditionally is the single
   * terminate path on dev and prod. logError returns early outside dev mode, so relying on it
   * left a prod failure with no status, and never re-equalised the batch counters (#4919).
   */
  private async stampFailure(
    account: AccountView,
    error: unknown,
    level: string,
    status: SyncStatusEnum,
  ): Promise<void> {
    // The dev-mode log gets the raw rejection, so an HTTP error's status/url/body stay visible.
    await this.glossSyncService.logError([account], error as Error, level, null);
    await this.glossSyncService.terminateSync([account], status, toError(error));
  }

  /** Saves a Balance Alignment Transaction (BAT) for the account using its plugin-provided
   * balance. Returns false (after stamping cantSaveCBAT) on failure. */
  private async savePluginCBAT(account: AccountView): Promise<boolean> {
    try {
      this.appState.startProcess("importing-data", `Generating CBAT of ${account.displayName}`);

      const balanceOnAccount = account.balance ?? 0;
      const transactionsTotal = this.transactionStore.enrichTransaction
        .collection()
        .filter((t) => t.accountLink.id === account.id)
        .reduce((acc, tran) => acc + tran.c_valuation.value.amount, 0);

      if (balanceOnAccount.toFixed(2) !== transactionsTotal.toFixed(2)) {
        const cBat = this.createPluginCBAT(balanceOnAccount, transactionsTotal, account);
        await this.transactionStore.transaction.save(cBat);
      }

      this.appState.endProcess("importing-data");
      return true;
    } catch (error) {
      await this.stampFailure(account, error, "Creating CBAT", SyncStatusEnum.cantSaveCBAT);
      this.appState.endProcess("importing-data");
      return false;
    }
  }

  private createPluginCBAT(
    balanceOnAccount: number,
    transactionsTotal: number,
    account: AccountView,
  ): TransactionView {
    const transaction = new TransactionView();

    transaction.glossPostDate = GlossDateFactory.createFromDate(new Date());
    transaction.bankImportedBalance = balanceOnAccount;
    transaction.bankImportedBalanceOrigin = Origin.plugin;
    transaction.quantity = GlossQuantityUtils.fromModel({
      cvrt: 1,
      ccy: "AUD",
      cvsym: "",
      qty: {
        amt: 0,
        sym: "AUD",
        prcs: 8,
      },
    });
    transaction.sourceLink = { id: null };
    transaction.accountLink = { id: account.id };
    transaction.allocations = AllocationUtils.createDefaultAllocations(0);
    transaction.description = `CBAT ( Balance on Account : ${balanceOnAccount}, transactions total(BATS included): ${transactionsTotal})`;
    transaction.valuationPrice = null;
    transaction.linkedTo = [];
    transaction.definition = TransactionStatusEnum.closing;
    transaction.kind = "";

    enrichTransaction(transaction);
    return transaction;
  }
}
